package com.crowpay.utils

import com.lightningkite.kiteui.reactive.*

/**
 * A wrapper around [HashMap] that signals its listeners whenever it is mutated
 * */
class SignalingMap<K, V>(private val map: HashMap<K, V>): MutableMap<K, V> by map, ImmediateWritable<Map<K, V>>, BaseListenable() {
    constructor() : this(HashMap())
    override var value: Map<K, V>
        get() = map
        set(value) {
            if (value != map) {
                map.clear()
                map.putAll(value)
                invokeAllListeners()
            }
        }

    val entriesReadable: ImmediateReadable<Set<Map.Entry<K, V>>> =
        object : ImmediateReadable<Set<Map.Entry<K, V>>>, Listenable by this {
            override val value: Set<Map.Entry<K, V>> get() = map.entries
        }

    val keysReadable: ImmediateReadable<Set<K>> =
        object : ImmediateReadable<Set<K>>, Listenable by this {
            override val value: Set<K> get() = map.keys
        }

    val valuesReadable: ImmediateReadable<Collection<V>> =
        object : ImmediateReadable<Collection<V>>, Listenable by this {
            override val value: Collection<V> get() = map.values
        }

    private inline fun <T> signal(operation: MutableMap<K, V>.()->T) = map.operation().also { invokeAllListeners() }

    override fun clear() = signal { clear() }
    override fun remove(key: K): V? = map.remove(key).also { if (it != null) invokeAllListeners() }
    override fun putAll(from: Map<out K, V>) = signal { putAll(from) }
    override fun put(key: K, value: V): V? = signal { put(key, value) }

    inner class ElementWritable(private val key: K) : ImmediateWritable<V?>, BaseListenable() {
        override var value: V? = map[key]
            set(value) {
                if (value == field) return

                field = value

                if (value == null) remove(key)
                else put(key, value)

                invokeAllListeners()
            }

        private var remover: (() -> Unit)? = null
        override fun activate() {
            remover = this@SignalingMap.addListener {
                value = map[key]
            }
        }
        override fun deactivate() {
            remover?.invoke()
        }
    }

    fun getWritable(key: K): ImmediateWritable<V?> = ElementWritable(key)
    fun getWritable(key: ReactiveContext.()->K): Writable<V?> =
        shared {
            this@SignalingMap.invoke()[key()]
        }.withWrite {
            if (it == null) remove(key()) else put(key(), it)
        }
}